import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import VerifiedBadge from "@/components/VerifiedBadge";
import { api, getBackendUrl } from "@/lib/api";
import {
  ArrowLeft, IdentificationBadge, LinkedinLogo, MapPin,
  Briefcase, FilePdf, FileArchive, CheckCircle, CalendarBlank, ArrowRight,
} from "@phosphor-icons/react";

/* v2.90 · Public PE detail page — reachable from the directory grid.
   Renders `profile_live` only, so pending draft edits stay hidden. */

export default function DirectoryPEDetail() {
  const { id } = useParams();
  const [pe, setPe] = useState(null);
  const [err, setErr] = useState("");
  const assetUrl = (ref) => ref ? `${getBackendUrl()}/api/pe/assets/${ref}` : "";

  useEffect(() => {
    api.get(`/directory/pes/${id}`)
      .then(({ data }) => setPe(data))
      .catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [id]);

  if (err) {
    return (
      <div className="industrial min-h-screen bg-[#0A0C10]">
        <Topbar />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
          <Link to="/directory" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={14} /> directory</Link>
          <div className="mt-6 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 text-sm text-[#FF8A85]" data-testid="dir-detail-error">{err}</div>
        </div>
      </div>
    );
  }
  if (!pe) {
    return (
      <div className="industrial min-h-screen bg-[#0A0C10]">
        <Topbar />
        <div className="p-10 text-sm font-mono text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <Link to="/directory" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="dir-detail-back">
          <ArrowLeft size={14} /> back to directory
        </Link>

        {/* Hero */}
        <div className="mt-6 flex items-start gap-5 flex-wrap">
          <div className="w-24 h-24 border border-white/10 bg-black/40 overflow-hidden flex-none">
            {pe.picture_ref
              ? <img src={assetUrl(pe.picture_ref)} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-gray-600"><IdentificationBadge size={50} weight="duotone" /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-3xl md:text-4xl font-black">
                {pe.name}
                <VerifiedBadge verified={pe.email_verified} size={20} />
              </h1>
              <span className="inline-flex items-center gap-1 border border-emerald-400/40 text-emerald-400 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">
                <CheckCircle size={10} weight="fill" /> vetted
              </span>
            </div>
            <div className="mt-1 text-sm font-mono text-gray-400 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {pe.region ? `${pe.region}, ${pe.country}` : pe.country || "—"}</span>
              <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {pe.years_of_practice} years of practice</span>
              {pe.linkedin_url && (
                <a href={pe.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#00E5FF] hover:underline" data-testid="dir-detail-linkedin">
                  <LinkedinLogo size={12} /> LinkedIn
                </a>
              )}
            </div>
            {/* v3.2.21 · Book a meeting CTA — visible only when the PE has a URL on file. */}
            {pe.booking_url && /^https?:\/\//i.test(pe.booking_url) && (
              <>
                <a
                  href={pe.booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-4 py-2 hover:bg-[#00E5FF]/90 transition-colors font-mono text-sm tracking-wide"
                  data-testid="dir-detail-book-meeting"
                >
                  <CalendarBlank size={16} weight="fill" /> Book a meeting <ArrowRight size={14} weight="bold" />
                </a>
                {/* v3.2.23 · Availability preview — embed public scheduler widget under the CTA. */}
                <AvailabilityPreview url={pe.booking_url} />
              </>
            )}
          </div>
        </div>

        {pe.bio && (
          <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="dir-detail-bio">
            <div className="label-tag mb-2">// about</div>
            <p className="text-sm text-gray-200 whitespace-pre-wrap">{pe.bio}</p>
            {pe.hobbies && <p className="mt-3 text-xs text-gray-500 font-mono">Hobbies: {pe.hobbies}</p>}
          </section>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="border border-white/10 bg-[#12151C] p-5">
            <div className="label-tag mb-2">// specializations</div>
            <div className="flex flex-wrap gap-1">
              {(pe.specializations || []).map((s) => (
                <span key={s} className="text-xs font-mono border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] px-2 py-0.5">{s}</span>
              ))}
              {(pe.specializations || []).length === 0 && <span className="text-xs text-gray-500">—</span>}
            </div>
          </section>
          <section className="border border-white/10 bg-[#12151C] p-5">
            <div className="label-tag mb-2">// competencies</div>
            <div className="text-sm text-gray-300 leading-relaxed">
              {(pe.competencies || []).join(" · ") || <span className="text-gray-500">—</span>}
            </div>
          </section>
        </div>

        {pe.certifications?.length > 0 && (
          <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="dir-detail-certs">
            <div className="label-tag mb-3">// certifications &amp; licenses</div>
            <ul className="space-y-1.5">
              {pe.certifications.map((c, i) => (
                <li key={i} className="text-sm text-gray-200">
                  <span className="text-white font-medium">{c.name}</span>
                  {c.number && <span className="text-gray-500"> · {c.number}</span>}
                  {c.issued && <span className="text-gray-500"> · issued {c.issued}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {pe.portfolio?.length > 0 && (
          <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="dir-detail-portfolio">
            <div className="label-tag mb-3">// portfolio</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {pe.portfolio.map((it) => (
                <a key={it.ref} href={assetUrl(it.ref)} target="_blank" rel="noreferrer"
                   className="border border-white/10 bg-black/40 p-2 hover:border-[#00E5FF]/40 block">
                  {it.content_type?.startsWith("image/")
                    ? <img src={assetUrl(it.ref)} alt="" className="w-full h-24 object-cover" />
                    : <div className="h-24 flex items-center justify-center text-gray-500"><FilePdf size={32} weight="duotone" /></div>}
                  <div className="mt-2 text-[11px] font-mono truncate" title={it.title}>{it.title}</div>
                </a>
              ))}
            </div>
          </section>
        )}

        {pe.templates?.length > 0 && (
          <section className="mt-6 border border-white/10 bg-[#12151C] p-5" data-testid="dir-detail-templates">
            <div className="label-tag mb-3">// template packs</div>
            <ul className="space-y-1">
              {pe.templates.map((it) => (
                <li key={it.ref} className="text-xs font-mono">
                  <a href={assetUrl(it.ref)} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline inline-flex items-center gap-1">
                    <FileArchive size={12} /> {it.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

// v3.2.23 · Small embedded availability preview under Book a Meeting.
// Supports Calendly + Cal.com public schedulers with an iframe embed;
// falls back to a plain link chip for any other scheduler URL.
function AvailabilityPreview({ url }) {
  const [collapsed, setCollapsed] = useState(true);
  if (!url) return null;
  let host = "";
  try { host = new URL(url).hostname.toLowerCase(); } catch { return null; }

  const isCalendly = host === "calendly.com" || host.endsWith(".calendly.com");
  const isCalCom   = host === "cal.com"      || host.endsWith(".cal.com");
  const isEmbeddable = isCalendly || isCalCom;

  if (!isEmbeddable) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-mono text-gray-500"
           data-testid="availability-preview-generic">
        <CalendarBlank size={12} /> Availability shown on {host}
      </div>
    );
  }

  // Calendly + Cal.com both support ?embed_domain / ?embed=1 style params.
  const embedUrl = isCalendly
    ? `${url}${url.includes("?") ? "&" : "?"}embed_domain=${window.location.hostname}&hide_gdpr_banner=1&hide_landing_page_details=1`
    : `${url}${url.includes("?") ? "&" : "?"}embed=preview`;

  return (
    <div className="mt-4 max-w-2xl" data-testid="availability-preview">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="inline-flex items-center gap-1 text-[10px] font-mono text-gray-400 hover:text-[#00E5FF] uppercase tracking-wider mb-2"
        data-testid="availability-preview-toggle"
        aria-expanded={!collapsed}
      >
        {collapsed ? "▸ show next-open slots" : "▾ hide preview"} · via {isCalendly ? "Calendly" : "Cal.com"}
      </button>
      {!collapsed && (
        <div className="border border-white/10 bg-white/[0.02] overflow-hidden">
          <iframe
            src={embedUrl}
            title={`Next available slots · ${host}`}
            className="w-full h-[480px] bg-white"
            loading="lazy"
            data-testid="availability-preview-iframe"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      )}
    </div>
  );
}

